import { Link } from "react-router-dom";
import LegalLayout from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="February 20, 2026" testid="privacy-page">
      <p>
        This Privacy Policy explains how Clinico Translate, a product of Wellness and
        Lifestyle Strategies LLC (&quot;we&quot;, &quot;us&quot;, the
        &quot;Service&quot;), collects, uses, and safeguards information when you use our website
        and clinical translation application at clinicotranslate.com. By using the Service, you
        agree to the practices described here.
      </p>

      <h2>1. Who this policy applies to</h2>
      <p>
        Clinico Translate is a productivity tool for licensed occupational, speech, and physical
        therapists (and their assistants). The account holder — the therapist or their employing
        clinic — is our direct customer and is responsible for the lawful use of any patient
        information entered into the Service.
      </p>

      <h2>2. Information we collect</h2>
      <h3>a. Information you provide</h3>
      <ul>
        <li><strong>Account data:</strong> name, email address, discipline, hashed password, and (for paid plans) billing details processed by Stripe.</li>
        <li><strong>Patient records you create:</strong> nickname/label, preferred language, notes, and translation transcripts you choose to save.</li>
        <li><strong>Session content:</strong> spoken audio and text you submit for real-time transcription and translation.</li>
        <li><strong>Support messages:</strong> anything you send us via the Report a Problem form or email.</li>
      </ul>
      <h3>b. Information collected automatically</h3>
      <ul>
        <li>Device, browser, and IP address for security and session management.</li>
        <li>Usage logs (e.g., pages accessed, translation counts) to operate and improve the Service.</li>
        <li>A secure, httpOnly authentication cookie so you stay signed in.</li>
      </ul>

      <h2>3. How we use information</h2>
      <ul>
        <li>To deliver real-time translation, transcription, and text-to-speech results back to you.</li>
        <li>To authenticate you, manage devices/sessions, and prevent abuse.</li>
        <li>To process subscription payments and send billing receipts.</li>
        <li>To send transactional emails (welcome, receipts, password resets, trial reminders, security alerts).</li>
        <li>To respond to your support requests and improve the Service.</li>
      </ul>
      <p>We do <strong>not</strong> sell personal information, and we do not use patient content to train AI models.</p>

      <h2>4. HIPAA &amp; patient data</h2>
      <p>
        Clinico Translate is engineered as a HIPAA-ready workflow tool: patient notes are stored
        encrypted, authentication uses secure httpOnly cookies, and access is device-scoped. However,
        certain third-party model providers we route translations through may not have executed a
        Business Associate Agreement (BAA) with us. Until a BAA is in place with every downstream
        processor, you should:
      </p>
      <ul>
        <li>Use patient <em>labels</em> or first-name-only, not full PHI, when creating records.</li>
        <li>Confirm you have patient authorization to record and translate the session.</li>
        <li>Follow your employer&#39;s policies and applicable state and federal law.</li>
      </ul>
      <p>
        You, the licensed therapist, remain the covered entity and are ultimately responsible for
        HIPAA compliance in your practice.
      </p>

      <h2>5. Third-party service providers</h2>
      <p>To operate the Service we share limited data with vetted vendors, including:</p>
      <ul>
        <li><strong>Anthropic (Claude):</strong> translation model provider.</li>
        <li><strong>OpenAI:</strong> speech-to-text (Whisper) and text-to-speech.</li>
        <li><strong>Stripe:</strong> subscription billing and payment card processing.</li>
        <li><strong>Resend:</strong> transactional email delivery.</li>
        <li><strong>MongoDB Atlas:</strong> encrypted database hosting.</li>
      </ul>
      <p>These providers process data only to perform their function and are bound by their own privacy and security terms.</p>

      <h2>6. Data retention</h2>
      <ul>
        <li>Account and patient records remain until you delete them or close the account.</li>
        <li>Deleting a patient permanently removes their notes and transcripts from active storage.</li>
        <li>Closing your account triggers cascading deletion of your patient records, billing linkage, and support history within 30 days.</li>
        <li>Backups may retain data for up to 30 additional days before being overwritten.</li>
      </ul>

      <h2>7. Security</h2>
      <p>
        We use TLS everywhere, encrypted at-rest storage, bcrypt-hashed passwords, httpOnly
        authentication cookies, device-scoped sessions with revocation, and least-privilege access
        controls. No system is perfectly secure — please report any suspected vulnerability to us
        immediately at <a href="mailto:BrittneyM@clinicotranslate.com">BrittneyM@clinicotranslate.com</a>.
      </p>

      <h2>8. Your rights</h2>
      <p>Depending on where you live, you may have the right to access, correct, export, or delete your personal information. To exercise any of these rights, email us at <a href="mailto:BrittneyM@clinicotranslate.com">BrittneyM@clinicotranslate.com</a>. We will respond within 30 days.</p>

      <h2>9. Children</h2>
      <p>The Service is intended for licensed clinicians, not for children under 13. We do not knowingly collect personal information from children directly.</p>

      <h2>10. International users</h2>
      <p>Clinico Translate is operated in the United States. If you access the Service from outside the U.S., your information will be transferred to and processed in the U.S.</p>

      <h2>11. Changes to this policy</h2>
      <p>We may update this Privacy Policy from time to time. Material changes will be announced by email or in-app notice before taking effect.</p>

      <h2>12. Contact</h2>
      <p>
        Questions about this policy? Email <a href="mailto:BrittneyM@clinicotranslate.com">BrittneyM@clinicotranslate.com</a>.
        See our <Link to="/terms">Terms of Service</Link> for the terms that govern your use of the Service.
      </p>
    </LegalLayout>
  );
}
